import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  DataTableEndlessPaginated,
  EmptyStateProps,
} from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { AuthenticatedRoutes } from '@/router/routes';

import { Client } from '../../types';

interface ClientsTableProps {
  table: Table<Client>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  isAnyFilterApplied?: boolean;
  totalCount?: number;
  handleRowClick: (client: Client) => void;
  getRowHref?: (client: Client) => string | undefined;
  stickyHeader?: boolean;
  fixedHeight?: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
}

export const ClientsTable: React.FC<ClientsTableProps> = ({
  isAnyFilterApplied,
  totalCount,
  handleRowClick,
  getRowHref,
  stickyHeader = false,
  fixedHeight = false,
  tableId,
  onResizeEnd,
  ...paginatedTableProps
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['authenticated.clients', 'common']);

  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    // If there's any filter but no results, show "No results found"
    if (isAnyFilterApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    // If there are no clients at all (total count is 0), show empty state with buttons
    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start px-8 text-left">
              <div className="flex flex-col">
                <div className="text-sm font-medium">
                  {t('authenticated.clients:table.noClientsYet')}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('authenticated.clients:table.noClientsDescription')}
                </div>
                <Button
                  size="sm"
                  className="mt-3 w-max"
                  onClick={() => navigate(`${AuthenticatedRoutes.Clients}/new`)}
                >
                  <CirclePlus />
                  {t('authenticated.clients:addClient')}
                </Button>
              </div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginated
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      onRowClick={handleRowClick}
      getRowHref={getRowHref}
      stickyHeader={stickyHeader}
      fixedHeight={fixedHeight}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
    />
  );
};
